import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/Card';
import { CopyButton } from '../components/CopyButton';
import { OptimisationReadout } from '../components/presence/OptimisationReadout';
import { ScoreRing } from '../components/ScoreRing';
import {
  ApiError,
  type PresenceGenerateResult,
  type PresenceOptimisation,
  type PresencePaste,
  type PresenceProfileResult,
  PresenceStillWorkingError,
  generatePresence,
  getPresenceGenerateStatus,
  resumePresenceGenerateLoop,
  saveSavedProfile,
  saveTargetRoles,
} from '../lib/api';
import {
  ROLE_SAVED_NOTE,
  resolvePasteText,
  scoreCardNote,
  scoreDisplay,
  shouldShowDeclaredRolesFallback,
  shouldShowRecommendedTitles,
} from '../lib/presenceHonestRender';
import {
  type PresenceGenerateState,
  INITIAL_GENERATE_STATE,
  beginGenerate,
  failGenerate,
  generateLoadingCopy,
  isGenerateDisabled,
  markPrewarmSettled,
  markRunStarted,
  markStillWorking,
  resolveGenerateResponse,
  resumeGenerate,
  resumingCopy,
  stillWorkingCopy,
} from '../lib/presenceGenerateState';
import {
  clearPresenceRun,
  readPresenceRun,
  writePresenceRun,
} from '../lib/presenceRunStore';
import { addRoleToList, isRoleAddNoop, removeRoleFromList } from '../lib/roleListEdit';
import { usePresencePrewarm } from '../lib/usePresencePrewarm';
import { useProfilesData } from '../lib/useProfilesData';
import styles from './Profiles.module.css';

/**
 * Profiles (Get Found) tab — Wave-1 C1 REAL-DATA rework
 * (docs/DEMO-WALKTHROUGH-issues.md #4). Previously rendered
 * placeholderProfiles UNCONDITIONALLY for every signed-in user — DB-verified
 * proof: a fabricated persona headline (the exact invented name/company are
 * recorded in issue #4 itself), a made-up score, and the same placeholder
 * title chips as Home's fake jobs, shown as if they were this user's own Get
 * Found profile. That is the NO-INVENTION violation CLAUDE.md's foundational
 * law forbids. (This file's own noFabricatedPlaceholders.guard.test.ts
 * mechanically proves those exact strings never appear here again — this doc
 * comment deliberately does not repeat them.)
 *
 * Every value below comes from useProfilesData (real GET /presence/
 * target-roles, GET /presence/profiles, GET /presence/profiles/:id, GET
 * /presence/platforms — all EXISTING, LOCKED presence routes; zero backend
 * changes). placeholderData.ts is no longer imported by this file at all.
 *
 * SCOPE NOTE (per this lane's own brief): this is the REAL-DATA pass, not a
 * concept redesign. The prior mockup's LinkedIn-completion-percentage /
 * headline-diff / Indeed-recruiter-toggle mechanism has no real backend
 * behind it (no per-platform completeness score, no accepted-headline
 * persistence, no recruiter-visibility toggle) — rendering that SHAPE with
 * real values bolted in would still be dishonest, since it implies a
 * mechanism that doesn't exist. So this rework shows exactly what IS real:
 * the saved profile's score, its actual generated headline + recommended
 * titles, and the user's declared target roles — a plainer screen than the
 * mockup, honestly. The concept-revisit docs/DEMO-WALKTHROUGH-issues.md #4
 * flags ("does the whole screen concept feel right") stay open for a later
 * design pass — see docs/design/get-found-plan-2026-07-05.md's G2.
 *
 * M5 (2026-07-04): usePresencePrewarm fires POST /presence/prewarm on mount
 * for a user who already has a real corpus + saved target roles — unchanged
 * by this rework, still additive and fire-and-forget.
 *
 * G0 (2026-07-05, docs/design/get-found-plan-2026-07-05.md — the tactical
 * wire): wires this client to the two working-but-unreached endpoints lens A
 * found (PUT /presence/target-roles, POST /presence/generate — both EXIST
 * and are LOCKED, unedited here). Three fixes, all additive client wiring,
 * zero backend/locked-file changes:
 *   1. Add/remove role chips, wired to saveTargetRoles (optimistic update +
 *      error rollback). A role edit does NOT claim the Jobs tab updates —
 *      the declare->watchlist flywheel re-derive is G1 (a separate, LOCKED-
 *      file, per-change-permission patch), so the honest confirmation is
 *      ROLE_SAVED_NOTE, never an overclaim of instant effect.
 *   2. The Generate button the score card's own copy has always promised but
 *      never rendered (Break 2 in lens A) — wired to generatePresence, with
 *      the honest idle/prewarmed/generating/served/blocked/failed state
 *      machine (presenceGenerateState.ts) so a blocked lint-gate response
 *      renders its REAL reason instead of the R125 "silent nothing" class.
 *   3. Every rendered value stays exactly what was served — score "—" until
 *      real, recommendedTitles hidden (not placeholder-filled) when absent —
 *      unchanged from C1, now shared with presenceHonestRender.ts so the
 *      decisions are independently pure-tested.
 */
export function Profiles() {
  const data = useProfilesData();
  usePresencePrewarm(true);

  const [generateState, setGenerateState] = useState<PresenceGenerateState>(
    INITIAL_GENERATE_STATE,
  );
  const [generateResult, setGenerateResult] = useState<PresenceGenerateResult | null>(null);
  const [roleDraft, setRoleDraft] = useState('');
  const [roleSaveNote, setRoleSaveNote] = useState<string | null>(null);
  const [roleSaveError, setRoleSaveError] = useState<string | null>(null);
  // WIRE-AUDIT fix #5 (docs/design/wire-audit-2026-07-05.md): the platform is
  // the USER'S pick — previously hardcoded to platforms[0] with the fetched
  // array unused (the fidelity Jason flagged as lost). null = no explicit pick
  // yet → falls to the first served platform, same default as before.
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  // WIRE-AUDIT fix #4: generate now SAVES (a generated profile previously
  // lived only in React state and vanished on refresh). These two report that
  // save honestly — a save failure never masquerades as a generate failure.
  const [profileSaveNote, setProfileSaveNote] = useState<string | null>(null);
  const [profileSaveError, setProfileSaveError] = useState<string | null>(null);

  // The initial data load finishing is this screen's own "prewarm settled"
  // moment (usePresencePrewarm's mount effect resolves at essentially the
  // same time) — markPrewarmSettled is a pure no-op once past idle, so this
  // effect only ever fires the one real idle->prewarmed transition.
  useEffect(() => {
    if (!data.loading) setGenerateState((s) => markPrewarmSettled(s));
  }, [data.loading]);

  const latestSummary = data.savedProfiles[0] ?? null;
  const isColdStart = !data.loading && data.savedProfiles.length === 0 && data.targetRoles.length === 0;
  const currentPlatform =
    selectedPlatform ?? data.platforms[0]?.platform ?? latestSummary?.platform ?? null;

  // W23 RESUME-ON-RETURN (docs/WALK-FINDINGS-2026-07-21.md). A Get Found
  // generation now runs SERVER-SIDE past this screen's lifetime, so a user who
  // navigates away — or whose mobile browser re-launches the SPA shell when
  // they come back to a backgrounded tab — would otherwise return to a screen
  // that has forgotten a run which is still going, or has since finished and
  // banked its profile. This rejoins that run by id (presenceRunStore) and
  // polls the SAME status endpoint; it never starts a second generation.
  // A 404 means the run genuinely isn't there to watch any more — clear it and
  // sit at the normal ready state rather than raising a failure banner about
  // an action the user did not just take.
  useEffect(() => {
    if (data.loading || !currentPlatform) return;
    const inFlight = readPresenceRun(currentPlatform);
    if (!inFlight) return;

    let cancelled = false;
    setGenerateState(resumeGenerate(inFlight.runId));
    void (async () => {
      try {
        const result = await resumePresenceGenerateLoop(
          inFlight.runId,
          getPresenceGenerateStatus,
          (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
        );
        if (!cancelled) await settleGenerated(result);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          clearPresenceRun();
          setGenerateState((s) => markPrewarmSettled({ ...s, status: 'idle' }));
          return;
        }
        handleGenerateFailure(err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // settleGenerated/handleGenerateFailure are stable per render and read
    // only state they set; re-running this effect on every render would
    // re-attach a duplicate poll loop to the same run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.loading, currentPlatform]);

  async function handleAddRole() {
    if (isRoleAddNoop(data.targetRoles, roleDraft)) {
      setRoleDraft('');
      return;
    }
    const next = addRoleToList(data.targetRoles, roleDraft);
    setRoleDraft('');
    await commitRoleEdit(next);
  }

  async function handleRemoveRole(role: string) {
    const next = removeRoleFromList(data.targetRoles, role);
    if (next === data.targetRoles) return;
    await commitRoleEdit(next);
  }

  // Optimistic update + honest error recovery: data.refresh() re-reads
  // target-roles from the server on EVERY commit (success or failure), so
  // the served truth always wins — a failed save never leaves the screen
  // showing an un-persisted list as if it had saved.
  async function commitRoleEdit(next: string[]) {
    setRoleSaveError(null);
    setRoleSaveNote(null);
    try {
      await saveTargetRoles(next);
      await data.refresh();
      setRoleSaveNote(ROLE_SAVED_NOTE);
    } catch (err) {
      await data.refresh();
      setRoleSaveError(err instanceof Error ? err.message : String(err));
    }
  }

  // W23 (docs/WALK-FINDINGS-2026-07-21.md) — the ONE place a finished run
  // settles, shared by a fresh generate and the resume-on-return path below,
  // so the two can never drift into disagreeing about what "done" looks like.
  async function settleGenerated(result: PresenceGenerateResult) {
    setGenerateResult(result);
    setGenerateState(resolveGenerateResponse(result));
    clearPresenceRun();
    if (result.blocked === true) return;
    if (result.saved) {
      // W23: the SERVER banked this before it ever answered
      // (composeAndBankPresence). Saving again from here would write a
      // duplicate version on every single generate — and, more to the point,
      // the client-side save is exactly what his 67-second run never got to
      // do. The bank no longer depends on this tab surviving.
      await data.refresh();
      setProfileSaveNote('Saved — this version is in your history now.');
      return;
    }
    // No `saved` field = an older API build with no server-side bank. Keep
    // the original client-side save so this screen never silently loses a
    // version against a mixed deploy. WIRE-AUDIT fix #4's original path,
    // unchanged: the server recomputes the optimisation, never trusting a
    // client score; a save failure is reported as exactly that, and the
    // generate above still succeeded and stays rendered.
    try {
      await saveSavedProfile({
        platform: currentPlatform ?? '',
        targetRoles: data.targetRoles,
        profile: result.profile,
        skills: result.profile.skills ?? [],
      });
      await data.refresh();
      setProfileSaveNote('Saved — this version is in your history now.');
    } catch (saveErr) {
      setProfileSaveError(saveErr instanceof Error ? saveErr.message : String(saveErr));
    }
  }

  // W23 — the honest failure branch. A poll ceiling reached while the server
  // is STILL RUNNING is NOT a failure: the run continues, and the API banks
  // the profile before it answers. "Couldn't generate right now: Load failed"
  // over a server call that went on to succeed in 67 seconds is the exact lie
  // this lane exists to remove.
  function handleGenerateFailure(err: unknown) {
    if (err instanceof PresenceStillWorkingError) {
      setGenerateState(markStillWorking(err.runId));
      return;
    }
    clearPresenceRun();
    const message = err instanceof ApiError || err instanceof Error ? err.message : String(err);
    setGenerateState(failGenerate(message));
  }

  async function handleGenerate() {
    if (isGenerateDisabled(generateState, data.targetRoles.length) || !currentPlatform) return;
    const platform = currentPlatform;
    setGenerateState((s) => beginGenerate(s));
    setProfileSaveNote(null);
    setProfileSaveError(null);
    try {
      const result = await generatePresence(platform, data.targetRoles, (runId) => {
        // Recorded the INSTANT the server acks, before any waiting — the only
        // ordering that survives the user navigating away a second later.
        writePresenceRun(runId, platform);
        setGenerateState((s) => markRunStarted(s, runId));
      });
      await settleGenerated(result);
    } catch (err) {
      handleGenerateFailure(err);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.eyebrow}>
        <div className={styles.kicker}>Get found</div>
        <div className={styles.title}>Where recruiters look</div>
      </div>

      {data.loading ? (
        <div className={styles.status}>Loading your profile…</div>
      ) : isColdStart ? (
        <ColdStartProfiles />
      ) : (
        <>
          <RoleChips
            targetRoles={data.targetRoles}
            draft={roleDraft}
            onDraftChange={setRoleDraft}
            onAdd={handleAddRole}
            onRemove={handleRemoveRole}
            saveNote={roleSaveNote}
            saveError={roleSaveError}
          />

          {/* WIRE-AUDIT fix #5 — the platform picker (chips, not a form; rule
              #2 visual-first). Every platform the server actually serves is
              selectable; the active chip is the one Generate targets. Hidden
              when the server serves 0-1 platforms (nothing to pick). */}
          {data.platforms.length > 1 ? (
            <Card className={styles.roleCard}>
              <div className={styles.fieldLabel}>Generate for</div>
              <div className={styles.titleChips}>
                {data.platforms.map((p) => (
                  <button
                    key={p.platform}
                    type="button"
                    className={
                      p.platform === currentPlatform
                        ? `${styles.titleChip} ${styles.platformChip} ${styles.platformChipActive}`
                        : `${styles.titleChip} ${styles.platformChip}`
                    }
                    aria-pressed={p.platform === currentPlatform}
                    onClick={() => setSelectedPlatform(p.platform)}
                  >
                    {platformLabel(p.platform)}
                  </button>
                ))}
              </div>
            </Card>
          ) : null}

          <RealProfiles
            latestSummary={latestSummary}
            headline={generateResult?.profile.headline ?? data.latestProfile?.headline ?? null}
            recommendedTitles={
              generateResult?.profile.recommendedTitles ?? data.latestProfile?.recommendedTitles ?? []
            }
            targetRoles={data.targetRoles}
            hasEverGenerated={latestSummary !== null || generateResult !== null}
            score={generateResult?.optimisation?.score ?? latestSummary?.score ?? null}
            about={generateResult?.profile.about ?? data.latestProfile?.about ?? null}
            skills={generateResult?.profile.skills ?? data.latestProfile?.skills ?? []}
            lint={generateResult?.profile.lint ?? null}
            optimisation={generateResult?.optimisation ?? data.latestOptimisation ?? null}
            paste={generateResult?.profile.paste ?? data.latestProfile?.paste ?? null}
          />

          <GenerateAction
            state={generateState}
            targetRoleCount={data.targetRoles.length}
            onGenerate={handleGenerate}
          />

          {profileSaveNote ? <div className={styles.roleSaveNote}>{profileSaveNote}</div> : null}
          {profileSaveError ? (
            <div className={styles.roleSaveError}>
              Your profile generated fine, but saving this version failed: {profileSaveError}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * Honest cold state (#4's fix): a user with no saved Get Found profile AND
 * no declared target roles yet gets a truthful "generate your first profile"
 * prompt, never the placeholder's fabricated persona.
 */
function ColdStartProfiles() {
  return (
    <Card className={styles.scoreCard}>
      <div className={styles.scoreBody}>
        <div className={styles.scoreTitle}>Nothing built yet</div>
        <div className={styles.scoreNote}>
          Once you've told me a little about yourself, head to Refine to build your story — Get
          Found reads from that same story to write a profile that's actually true about you.
        </div>
        <Link to="/refine" className={styles.primaryBtn}>
          Start with Meira
        </Link>
      </div>
    </Card>
  );
}

interface RoleChipsProps {
  targetRoles: string[];
  draft: string;
  onDraftChange: (v: string) => void;
  onAdd: () => void;
  onRemove: (role: string) => void;
  saveNote: string | null;
  saveError: string | null;
}

/**
 * G0 fix #1 — add/remove declared roles in place. Each chip gets an × (calls
 * saveTargetRoles with the role filtered out); the input + "Add" button call
 * saveTargetRoles with the trimmed, deduped draft appended. A role change
 * does not claim the Jobs tab updates instantly — the flywheel re-derive is
 * G1 (locked-file, separate permission), so the confirmation copy
 * (ROLE_SAVED_NOTE) only ever says the roles are saved for next time.
 */
function RoleChips({ targetRoles, draft, onDraftChange, onAdd, onRemove, saveNote, saveError }: RoleChipsProps) {
  return (
    <Card className={styles.roleCard}>
      <div className={styles.fieldLabel}>Roles you've declared for Get Found</div>
      {targetRoles.length > 0 ? (
        <div className={styles.titleChips}>
          {targetRoles.map((role) => (
            <span key={role} className={styles.titleChip}>
              {role}
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => onRemove(role)}
                aria-label={`Remove ${role}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div className={styles.scoreNote}>No roles declared yet — add one below.</div>
      )}
      <div className={styles.addRoleRow}>
        <input
          type="text"
          className={styles.addRoleInput}
          placeholder="Add a role — e.g. Head of AI"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAdd();
            }
          }}
        />
        <button type="button" className={styles.addRoleBtn} onClick={onAdd}>
          Add
        </button>
      </div>
      {saveNote ? <div className={styles.roleSaveNote}>{saveNote}</div> : null}
      {saveError ? <div className={styles.roleSaveError}>Couldn't save that change: {saveError}</div> : null}
    </Card>
  );
}

interface RealProfilesProps {
  latestSummary: { platform: string; score: number | null; createdAt: string } | null;
  headline: string | null;
  recommendedTitles: string[];
  targetRoles: string[];
  hasEverGenerated: boolean;
  score: number | null;
  /** LANE JOB-TRUST G5 — the served About text (generateResult wins, else the
   *  recalled saved profile's own about) — null before anything has ever been
   *  generated, never a placeholder blurb. */
  about: string | null;
  /** LANE JOB-TRUST G5 — the served skill keywords, same generateResult-wins-
   *  else-recalled precedence as headline/recommendedTitles above. An empty
   *  array is honest "nothing served yet", not an error. */
  skills: string[];
  /** LANE JOB-TRUST G5 — the render-blocking gate's own au/sellShort/brevity
   *  verdict for the CURRENT generateResult only (null for a recalled saved
   *  profile — a recall never re-derives a session-specific lint verdict for
   *  a draft that already passed and was saved; only a live generate carries
   *  a lint readout worth showing). */
  lint: PresenceProfileResult['lint'] | null;
  /** LANE JOB-TRUST G4 — the OptimisationReadout port's data: generateResult's
   *  own optimisation, else the recalled saved profile's stored optimisation.
   *  Null when nothing has ever been generated/saved, or when the platform's
   *  criteria are genuinely unvalidated (OptimisationReadout renders that
   *  third honest state itself). */
  optimisation: PresenceOptimisation | null;
  /** GAP 3 — the server's platform-shaped copy-paste strings
   *  (renderPresencePaste, packages/schema/src/render/presence_paste.ts),
   *  SAME generateResult-wins-else-recalled precedence as every other field
   *  above. Null when nothing has ever been generated (the honest cold
   *  state — no copy buttons render), OR when the served profile predates
   *  this field being typed/persisted (an older saved row, or a generate
   *  response from before this widening) — in EITHER null case every copy
   *  button below falls back to the raw (unshaped) field text rather than
   *  disappearing, so copying still works, just without per-platform
   *  shaping (mirrors apps/web/src/pages/Presence.tsx's own `p.paste?.field
   *  ?? p.field` idiom at every one of its copy-button call sites). */
  paste: PresencePaste | null;
}

/**
 * The data-rich Profiles view. `score` is null when the platform's criteria
 * were unvalidated (or nothing has been generated yet) — rendered as the
 * honest "—" (scoreDisplay), never a fabricated number (mirrors
 * PresenceOptimisation's own three-honest-states law in apps/web).
 */
function RealProfiles({
  latestSummary,
  headline,
  recommendedTitles,
  targetRoles,
  hasEverGenerated,
  score,
  about,
  skills,
  lint,
  optimisation,
  paste,
}: RealProfilesProps) {
  return (
    <>
      <Card className={styles.scoreCard}>
        {score !== null ? (
          <ScoreRing pct={score} size={64} fontSize={24} />
        ) : (
          <div className={styles.scoreUnscored}>{scoreDisplay(score)}</div>
        )}
        <div className={styles.scoreBody}>
          <div className={styles.scoreTitle}>Your Get Found score</div>
          <div className={styles.scoreNote}>
            {scoreCardNote({
              hasEverGenerated,
              score,
              platformLabel: latestSummary ? platformLabel(latestSummary.platform) : null,
            })}
          </div>
        </div>
      </Card>

      {/* LANE JOB-TRUST G4 — the per-criterion optimisation readout, the
          "explanation of the metrics and algorithm each one uses" Jason asked
          for. Collapsible: the headline score/band/bar stay visible, the
          criteria rows fold behind a tap — mirrors apps/web's own Presence.tsx
          collapsible=true convention for this same component. */}
      {optimisation ? <OptimisationReadout opt={optimisation} collapsible /> : null}

      {/* GAP 3 — copy-all: the whole profile as ONE platform-shaped block
          (renderPresencePaste's `full`, lake-driven). Mirrors apps/web/src/
          pages/Presence.tsx's own copy-all row exactly. Only renders once a
          real paste has been served — never for the cold "nothing generated
          yet" state. */}
      {paste?.full ? (
        <Card className={styles.siteCard}>
          <div className={styles.copyAllRow}>
            <div className={styles.recruiterCopy}>
              The whole profile, shaped for {latestSummary ? platformLabel(latestSummary.platform) : 'this platform'} — ready to paste.
            </div>
            <CopyButton text={paste.full} label="entire profile" />
          </div>
        </Card>
      ) : null}

      {headline ? (
        <Card className={styles.siteCard}>
          <div className={styles.fieldLabelRow}>
            <div className={styles.fieldLabel}>Your current headline</div>
            <CopyButton text={resolvePasteText(paste?.headline, headline)} label="headline" />
          </div>
          <div className={styles.newLine}>{headline}</div>
        </Card>
      ) : null}

      {/* LANE JOB-TRUST G5 — the fuller generate render: the served About
          text, verbatim, whichever source it came from (a fresh generate or
          the recalled saved profile). */}
      {about ? (
        <Card className={styles.siteCard}>
          <div className={styles.fieldLabelRow}>
            <div className={styles.fieldLabel}>About</div>
            <CopyButton text={resolvePasteText(paste?.about, about)} label="about" />
          </div>
          <div className={styles.aboutBody}>{about}</div>
        </Card>
      ) : null}

      {/* LANE JOB-TRUST G5 — the served searchable-keyword skill chips. */}
      {skills.length > 0 ? (
        <Card>
          <div className={styles.fieldLabelRow}>
            <div className={styles.recruiterCopy}>Skills · the searchable keywords</div>
            <CopyButton text={resolvePasteText(paste?.skills, skills.join(', '))} label="skills" />
          </div>
          <div className={styles.titleChips}>
            {skills.map((s) => (
              <span key={s} className={styles.skillChip}>
                {s}
              </span>
            ))}
          </div>
        </Card>
      ) : null}

      {shouldShowRecommendedTitles(recommendedTitles) ? (
        <Card>
          <div className={styles.fieldLabelRow}>
            <div className={styles.recruiterCopy}>Titles I think you should be found for</div>
            <CopyButton
              text={resolvePasteText(paste?.recommendedTitles, recommendedTitles.join('\n'))}
              label="titles"
            />
          </div>
          <div className={styles.titleChips}>
            {recommendedTitles.map((title) => (
              <span key={title} className={styles.titleChip}>
                {title}
              </span>
            ))}
          </div>
        </Card>
      ) : shouldShowDeclaredRolesFallback(recommendedTitles, targetRoles) ? (
        <Card>
          <div className={styles.recruiterCopy}>Roles you've declared for Get Found</div>
          <div className={styles.titleChips}>
            {targetRoles.map((title) => (
              <span key={title} className={styles.titleChip}>
                {title}
              </span>
            ))}
          </div>
        </Card>
      ) : null}

      {/* LANE JOB-TRUST G5 — the render-blocking gate's own honest verdict for
          the CURRENT session's generate only (lint is null for a recalled
          saved profile, see RealProfilesProps' own doc comment). Mirrors
          apps/web's LintChips exactly: three honest chips, never a single
          collapsed pass/fail. */}
      {lint ? (
        <div className={styles.lintChipRow}>
          <span className={lint.au.passing ? styles.lintChipGood : styles.lintChipAmber}>
            {lint.au.passing ? 'AU spelling clean' : `AU · ${lint.au.totalIssues}`}
          </span>
          <span className={lint.sellShort.passing ? styles.lintChipGood : styles.lintChipAmber}>
            {lint.sellShort.passing ? 'no undersell' : 'undersell flagged'}
          </span>
          <span className={lint.brevity.withinBudget ? styles.lintChipGood : styles.lintChipAmber}>
            about {lint.brevity.wordCount}w
          </span>
        </div>
      ) : null}
    </>
  );
}

interface GenerateActionProps {
  state: PresenceGenerateState;
  targetRoleCount: number;
  onGenerate: () => void;
}

/**
 * G0 fix #2 — the real Generate button the score card's copy has always
 * pointed at. Renders every honest state the server can actually return:
 * a disabled/idle button before there's anything to generate against, a
 * working state (copy honestly hedged per generateLoadingCopy), the served
 * confirmation, and — the R125 regression class this must never repeat — a
 * BLOCKED response's real reason, never silence.
 *
 * W23 (docs/WALK-FINDINGS-2026-07-21.md; owner's word `Go-longops`): plus the
 * state that was MISSING and got rendered as a failure instead — `still-
 * working`, where the client's bounded poll has given up watching but the
 * server run is genuinely still going. It renders as progress (a note, not an
 * error), and the button stays disabled, because a second click would start a
 * duplicate run he pays for twice. The resume state gets its own line so a
 * rejoined run says so rather than looking like a fresh one.
 */
function GenerateAction({ state, targetRoleCount, onGenerate }: GenerateActionProps) {
  const disabled = isGenerateDisabled(state, targetRoleCount);
  const isWorking = state.status === 'generating';
  const isStillWorking = state.status === 'still-working';
  // Only a REJOINED run says so. A fresh click also carries a runId once its
  // ack lands, so the id alone cannot tell them apart — telling a first-time
  // clicker "picking up where we left off" would describe something that
  // never happened to them.
  const isResumed = isWorking && state.resumed;

  return (
    <Card className={styles.generateCard}>
      <button type="button" className={styles.primaryBtn} onClick={onGenerate} disabled={disabled}>
        {isWorking || isStillWorking
          ? generateLoadingCopy(state.status)
          : 'Generate your Get Found profile'}
      </button>
      {targetRoleCount === 0 ? (
        <div className={styles.scoreNote}>Add at least one declared role above before you generate.</div>
      ) : null}
      {isResumed ? <div className={styles.scoreNote}>{resumingCopy()}</div> : null}
      {isStillWorking ? <div className={styles.scoreNote}>{stillWorkingCopy()}</div> : null}
      {state.status === 'served' ? (
        <div className={styles.roleSaveNote}>Your profile is ready above — generate again anytime.</div>
      ) : null}
      {state.status === 'blocked' && state.blockedReason ? (
        <div className={styles.roleSaveError}>
          That draft didn't clear my own honesty check ({state.blockedReason.kind} in the{' '}
          {state.blockedReason.location}): "{state.blockedReason.text}". Try generating again — it
          often resolves on a retry.
        </div>
      ) : null}
      {state.status === 'failed' && state.errorMessage ? (
        <div className={styles.roleSaveError}>Couldn't generate right now: {state.errorMessage}</div>
      ) : null}
    </Card>
  );
}

/** Title-case a served platform id for display ("linkedin" -> "LinkedIn").
 *  Presentation only, matches apps/web/src/pages/Presence.tsx's own
 *  platformLabel helper — the id stays the real value, this only formats it. */
function platformLabel(id: string): string {
  const bare = id.includes(':') ? id.slice(id.indexOf(':') + 1) : id;
  const spaced = bare.replace(/[-_]+/g, ' ').trim();
  return spaced
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}
